import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { apiFetch } from "../../utils/apiFetch";
import DealHistory from "./DealHistory";
import DealSummarySections from "./DealSummarySections";
import {
  cancelAdminDeal,
  deleteAdminDeal,
  resetAdminDeal,
} from "./dealApi";
import "./global/global.css";

const LIST_PATH = "/admin/hitl/deals/all";
const RESET_ELIGIBLE = ["executing", "plan_proposed"];

const actionButtonStyle = (disabled, danger = false) => ({
  marginLeft: "8px",
  padding: "4px 12px",
  fontSize: "13px",
  borderRadius: "4px",
  border: `1px solid ${danger ? "#e57373" : "#bbb"}`,
  background: disabled ? "#f5f5f5" : "#fff",
  color: disabled ? "#bbb" : danger ? "#c62828" : "#555",
  cursor: disabled ? "not-allowed" : "pointer",
});

const AdminDealDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(
        `${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/admin/deals/${id}/`
      );
      if (!res.ok) {
        throw new Error(
          res.status === 404 ? "Deal not found" : "Failed to fetch deal details"
        );
      }
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Confirms, runs the action, and reports failures inline. `onSuccess` runs
  // only after the request succeeded.
  const runAction = async (prompt, action, onSuccess) => {
    if (!window.confirm(prompt)) return;
    setActionError(null);
    try {
      await action(id);
      await onSuccess();
    } catch (err) {
      setActionError(err.message);
    }
  };

  const heading = (subtitle) => (
    <div
      className="client-list-heading"
      style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: "16px" }}
    >
      <div className="lc">
        <h3 className="cl-rh">Deal Details</h3>
        <p className="cl-rh-sub">{subtitle}</p>
      </div>
    </div>
  );

  if (loading || error) {
    return (
      <div className="client-list-root">
        <div className="client-list-container">
          {heading(<Link to={LIST_PATH}>&larr; Back to All Deals</Link>)}
          <div className="cl-table-container">
            {loading ? (
              <p className="loading">Loading...</p>
            ) : (
              <p className="error">Error: {error}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const { deal, contacts, milestones, emails, documents, timeline, drafts } =
    data;
  const address = deal.display_address || deal.property_address;
  const cannotCancel = deal.status === "cancelled" || deal.status === "closed";
  const cannotReset = !RESET_ELIGIBLE.includes(deal.program_status);

  return (
    <div className="client-list-root">
      <div className="client-list-container">
        {heading(
          <>
            <Link to={LIST_PATH}>&larr; Back to All Deals</Link>
            {` · ${deal.realtor_email} · ${deal.status}`}
            {deal.program_status &&
              ` · ${deal.program_status.replace(/_/g, " ")}`}
          </>
        )}

        {actionError && (
          <div
            style={{
              margin: "16px 20px 0",
              padding: "10px 14px",
              backgroundColor: "#fce4ec",
              color: "#b71c1c",
              borderRadius: "6px",
              fontSize: "14px",
            }}
          >
            {actionError}
          </div>
        )}

        <div style={{ padding: "16px 20px 0", textAlign: "right" }}>
          <button
            disabled={cannotCancel}
            style={actionButtonStyle(cannotCancel)}
            onClick={() =>
              runAction(
                `Cancel deal at "${address}"? This will halt all pending milestones.`,
                cancelAdminDeal,
                load
              )
            }
          >
            Cancel
          </button>
          <button
            disabled={cannotReset}
            style={actionButtonStyle(cannotReset)}
            onClick={() =>
              runAction(
                `Reset deal at "${address}" to "Plan Proposed"? The realtor's next reply will re-enter the plan-confirmation flow.`,
                resetAdminDeal,
                load
              )
            }
          >
            Reset
          </button>
          <button
            style={actionButtonStyle(false, true)}
            onClick={() =>
              runAction(
                `Permanently delete deal at "${address}" and all associated data? This cannot be undone.`,
                deleteAdminDeal,
                () => history.push(LIST_PATH)
              )
            }
          >
            Delete
          </button>
        </div>

        <div style={{ padding: "0 20px 28px" }}>
          <DealSummarySections
            deal={deal}
            contacts={contacts}
            milestones={milestones}
          />
          <DealHistory
            dealId={id}
            emails={emails}
            documents={documents}
            timeline={timeline}
            drafts={drafts}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminDealDetail;
